/**
 * Act3WeekResult — 周结果展示（含双视角内心独白流式渲染）
 */
import React from 'react';
import { ArrowRight, TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface Act3WeekResultProps {
  week: number;
  narrative: string;
  commitmentA: number;
  commitmentB: number;
  energyA: number;
  energyB: number;
  stability: number;
  nameA: string;
  nameB: string;
  onContinue: () => void;
  // 双视角内心独白
  innerMonologueA?: string;
  innerMonologueB?: string;
  streamingMonologueSide?: 'A' | 'B' | null;
  streamingMonologueText?: string;
}

export const Act3WeekResult: React.FC<Act3WeekResultProps> = ({
  week,
  narrative,
  commitmentA,
  commitmentB,
  energyA,
  energyB,
  stability,
  nameA,
  nameB,
  onContinue,
  innerMonologueA,
  innerMonologueB,
  streamingMonologueSide,
  streamingMonologueText,
}) => {
  const monologueA = streamingMonologueSide === 'A' ? streamingMonologueText : innerMonologueA;
  const monologueB = streamingMonologueSide === 'B' ? streamingMonologueText : innerMonologueB;
  const isStreamingA = streamingMonologueSide === 'A';
  const isStreamingB = streamingMonologueSide === 'B';

  return (
    <div className="flex-1 flex items-center justify-center p-6">
      <div className="max-w-sm w-full bg-white rounded-2xl border border-[#E5E0D8] p-6 shadow-lg">
        <div className="text-center mb-4">
          <span className="text-[10px] text-[#9CA3AF]">第 {week} 周结果</span>
        </div>

        {/* Narrative */}
        <div className="bg-gradient-to-r from-[#F5EDE3] to-[#FAF6F1] rounded-xl p-4 mb-4">
          <p className="text-sm text-[#6B5B4F] leading-relaxed">{narrative}</p>
        </div>

        {/* 双视角内心独白 */}
        {(monologueA || monologueB) && (
          <div className="grid grid-cols-2 gap-2 mb-4">
            <MonologueBubble
              name={nameA}
              text={monologueA}
              isStreaming={isStreamingA}
              color="from-coral-500/10 to-coral-500/5"
              borderColor="border-coral-500/20"
            />
            <MonologueBubble
              name={nameB}
              text={monologueB}
              isStreaming={isStreamingB}
              color="from-purple-500/10 to-purple-500/5"
              borderColor="border-purple-500/20"
            />
          </div>
        )}

        {/* Current metrics */}
        <div className="space-y-2 mb-6">
          <MetricRow label={`${nameA} 承诺`} value={commitmentA} />
          <MetricRow label={`${nameB} 承诺`} value={commitmentB} />
          <MetricRow label={`${nameA} 精力`} value={energyA} />
          <MetricRow label={`${nameB} 精力`} value={energyB} />
          <MetricRow label="关系稳定性" value={stability} />
        </div>

        <button
          onClick={onContinue}
          className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-coral-500 to-amber-accent hover:from-coral-700 hover:to-coral-500 shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
        >
          继续 <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

const MonologueBubble: React.FC<{
  name: string;
  text?: string;
  isStreaming: boolean;
  color: string;
  borderColor: string;
}> = ({ name, text, isStreaming, color, borderColor }) => {
  if (!text && !isStreaming) return <div />;

  return (
    <div className={`bg-gradient-to-b ${color} border ${borderColor} rounded-xl p-3`}>
      <div className="text-[10px] text-[#9CA3AF] mb-1 font-medium">{name}的内心</div>
      <p className="text-xs text-[#6B5B4F] leading-relaxed italic">
        {text || ''}
        {isStreaming && <span className="inline-block w-0.5 h-3 bg-[#6B5B4F] ml-0.5 animate-pulse" />}
      </p>
    </div>
  );
};

const MetricRow: React.FC<{ label: string; value: number }> = ({ label, value }) => {
  const color = value >= 60 ? 'text-green-600' : value >= 30 ? 'text-amber-600' : 'text-red-500';
  const Icon = value >= 60 ? TrendingUp : value >= 30 ? Minus : TrendingDown;

  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px] text-[#6B7280]">{label}</span>
      <div className="flex items-center gap-1">
        <Icon size={10} className={color} />
        <span className={`text-xs font-bold ${color}`}>{value}</span>
      </div>
    </div>
  );
};

export default Act3WeekResult;
